import React from 'react';

/*
 * The text format used by dynamic form descriptions, instructions and the banner caption:
 * **bold**, *italic*, __underline__, ##large##, [link text](url) and lines starting with "- "
 * as bullets. Paragraphs are separated by a line break or two spaces.
 *
 * These are the renderers the public form page has always used; the admin editor
 * (RichTextEditor) writes this same format, so stored text renders unchanged.
 */

/** Splits stored text into its paragraphs, the same way the public page does. */
export const splitParagraphs = (text) => (text || '').split(/\r\n|\n| {2}/).filter((p) => p.trim());

export const isBulletLine = (line) => line.trim().startsWith('- ') || line.trim().startsWith('• ');

/** "1. text" or "1) text" (up to three digits, so a year like "2024. was great" stays a paragraph). */
const NUMBERED_RE = /^(\d{1,3})[.)]\s+/;
export const isNumberedLine = (line) => NUMBERED_RE.test(line.trim());
export const numberOf = (line) => Number(line.trim().match(NUMBERED_RE)[1]);
export const stripNumber = (line) => line.trim().replace(NUMBERED_RE, '');

// Bold is set in the page's full text colour and weight 700, so it reads as bold even inside
// muted text such as the banner caption.
const BOLD = { fontWeight: 700, color: 'rgba(0, 0, 0, 0.88)' };

export const parseInlineFormatting = (text) => {
  const parts = [];
  let keyCounter = 0;

  // Combined regex to match **bold**, ##large##, __underline__, *italic*, and [text](url)
  // eslint-disable-next-line no-useless-escape
  const formatRegex = /(\[([^\]]+)\]\(([^)]+)\)|\*\*.*?\*\*|\##.*?\##|__.*?__|(?<!\*)\*(?!\*).*?(?<!\*)\*(?!\*))/g;
  const matches = [...text.matchAll(formatRegex)];

  if (matches.length === 0) {
    return text;
  }

  let lastIndex = 0;
  matches.forEach((match) => {
    const fullMatch = match[0];
    const matchIndex = match.index;

    // Add text before the formatted part
    if (matchIndex > lastIndex) {
      parts.push(text.substring(lastIndex, matchIndex));
    }

    // Determine format type and extract content
    if (fullMatch.startsWith('[') && match[2] && match[3]) {
      // Hyperlink [text](url)
      const linkText = match[2];
      const url = match[3];
      const safeUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      parts.push(
        <a key={`link-${keyCounter++}`} href={safeUrl} target="_blank" rel="noopener noreferrer">
          {linkText}
        </a>
      );
    } else if (fullMatch.startsWith('**') && fullMatch.endsWith('**')) {
      // Bold text - check if inner content has other formatting
      const innerContent = fullMatch.slice(2, -2);
      if (innerContent.startsWith('##') && innerContent.endsWith('##')) {
        // Both bold and large
        const content = innerContent.slice(2, -2);
        parts.push(
          <strong key={`bold-large-${keyCounter++}`} style={BOLD}>
            <span style={{ fontSize: '1.3em', fontWeight: '700' }}>{content}</span>
          </strong>
        );
      } else {
        // Just bold - recursively parse in case there's other formatting inside
        const parsed = parseInlineFormatting(innerContent);
        parts.push(<strong key={`bold-${keyCounter++}`} style={BOLD}>{parsed}</strong>);
      }
    } else if (fullMatch.startsWith('##') && fullMatch.endsWith('##')) {
      // Large text - check if inner content has other formatting
      const innerContent = fullMatch.slice(2, -2);
      if (innerContent.startsWith('**') && innerContent.endsWith('**')) {
        // Both large and bold
        const content = innerContent.slice(2, -2);
        parts.push(
          <span key={`large-bold-${keyCounter++}`} style={{ ...BOLD, fontSize: '1.3em' }}>
            {content}
          </span>
        );
      } else {
        // Just large - recursively parse
        const parsed = parseInlineFormatting(innerContent);
        parts.push(<span key={`large-${keyCounter++}`} style={{ fontSize: '1.3em', fontWeight: '500' }}>{parsed}</span>);
      }
    } else if (fullMatch.startsWith('__') && fullMatch.endsWith('__')) {
      // Underline text
      const innerContent = fullMatch.slice(2, -2);
      const parsed = parseInlineFormatting(innerContent);
      parts.push(<u key={`underline-${keyCounter++}`}>{parsed}</u>);
    } else if (fullMatch.startsWith('*') && fullMatch.endsWith('*') && !fullMatch.startsWith('**')) {
      // Italic text (single asterisk)
      const innerContent = fullMatch.slice(1, -1);
      const parsed = parseInlineFormatting(innerContent);
      parts.push(<em key={`italic-${keyCounter++}`}>{parsed}</em>);
    }

    lastIndex = matchIndex + fullMatch.length;
  });

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

export const parseFormattedText = (text) => {
  // Parse text for formatting: bold (**), large (##), underline (__), italics (*)
  // First handle bullet points
  if (text.trim().startsWith('- ') || text.trim().startsWith('• ')) {
    return (
      <li style={{ marginLeft: '20px' }}>
        {parseInlineFormatting(text.replace(/^[-•]\s+/, ''))}
      </li>
    );
  }

  return parseInlineFormatting(text);
};

/**
 * Paragraphs, bullet lists ("- ") and numbered lists ("1. "), as used for field descriptions
 * (14px) and form instructions (16px).
 */
export const renderFormattedBlocks = (text, fontSize) => {
  const paragraphs = splitParagraphs(text);
  const elements = [];
  let list = null; // { kind: 'ul' | 'ol', start, items }
  let key = 0;

  const flush = () => {
    if (!list) return;
    const items = list.items.map((item, idx) => <li key={idx}>{parseFormattedText(item)}</li>);
    elements.push(
      list.kind === 'ol' ? (
        <ol key={`list-${key++}`} start={list.start} style={{ margin: '0.5em 0', paddingLeft: '24px' }}>{items}</ol>
      ) : (
        <ul key={`list-${key++}`} style={{ margin: '0.5em 0', paddingLeft: '20px' }}>{items}</ul>
      )
    );
    list = null;
  };

  paragraphs.forEach((paragraph, index) => {
    const trimmed = paragraph.trim();

    if (isBulletLine(trimmed)) {
      if (list && list.kind !== 'ul') flush();
      if (!list) list = { kind: 'ul', items: [] };
      list.items.push(trimmed.replace(/^[-•]\s+/, ''));
    } else if (isNumberedLine(trimmed)) {
      if (list && list.kind !== 'ol') flush();
      if (!list) list = { kind: 'ol', start: numberOf(trimmed), items: [] };
      list.items.push(stripNumber(trimmed));
    } else {
      flush();
      elements.push(
        <p key={`p-${key++}`} style={{ margin: index === 0 ? 0 : '1em 0 0 0', fontSize }}>
          {parseFormattedText(trimmed)}
        </p>
      );
    }
  });

  flush();
  return elements;
};
